import React from 'react';
import { Box, Typography, TextField, MenuItem, Divider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import i18n from 'i18';

export const DateTimeField = ({ data, field, onChange, ...props }) => {
    const { id, label, helpText, needDivider } = field;
    
    // Parse date and time data
    const dateTimeValue = data || null;
    const dateValue = dateTimeValue ? new Date(dateTimeValue) : null;
    
    // Check date validity and set default values
    const isValidDate = dateValue && !isNaN(dateValue.getTime());
    const hours = isValidDate ? dateValue.getHours().toString().padStart(2, '0') : '16';
    const minutes = isValidDate ? dateValue.getMinutes().toString().padStart(2, '0') : '00';

    // Generate hour options (00-23)
    const hourOptions = Array.from({ length: 24 }, (_, i) => 
        i.toString().padStart(2, '0')
    );

    // Generate minute options (00, 15, 30, 45)
    const minuteOptions = ['00', '15', '30', '45'];

    const handleDateChange = (newDate) => {
        if (newDate && !isNaN(newDate.getTime())) {
            // Check that date is valid
            const currentDateTime = dateValue || new Date();
            const updatedDateTime = new Date(newDate);
            
            // Check that updatedDateTime is also valid
            if (!isNaN(updatedDateTime.getTime())) {
                updatedDateTime.setHours(currentDateTime.getHours());
                updatedDateTime.setMinutes(currentDateTime.getMinutes());
                
                // Another check before calling toISOString
                if (!isNaN(updatedDateTime.getTime())) {
                    onChange(id, updatedDateTime.toISOString());
                }
            }
        } else if (newDate === null) {
            // Explicit null passed - clear field
            onChange(id, null);
        }
        // Ignore invalid dates
    };

    const handleTimeChange = (type, value) => {
        const currentDateTime = dateValue || new Date();
        
        // Check that current date is valid
        if (isNaN(currentDateTime.getTime())) {
            console.warn('Invalid current date in handleTimeChange');
            return;
        }
        
        const updatedDateTime = new Date(currentDateTime);
        
        if (type === 'hours') {
            const newHours = parseInt(value, 10);
            if (!isNaN(newHours) && newHours >= 0 && newHours <= 23) {
                updatedDateTime.setHours(newHours);
            }
        } else if (type === 'minutes') {
            const newMinutes = parseInt(value, 10);
            if (!isNaN(newMinutes) && newMinutes >= 0 && newMinutes <= 59) {
                updatedDateTime.setMinutes(newMinutes);
            }
        }
        
        // Check final date before sending
        if (!isNaN(updatedDateTime.getTime())) {
            onChange(id, updatedDateTime.toISOString());
        } else {
            console.warn('Invalid updated date in handleTimeChange');
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    {i18n.t(`columns.${label}`)}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                    {/* Date Section */}
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                            Date
                        </Typography>
                        <DatePicker
                            value={isValidDate ? dateValue : null}
                            onChange={handleDateChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    placeholder="Select end date"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                        }
                                    }}
                                />
                            )}
                        />
                    </Box>

                    {/* Time Section */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                Time
                            </Typography>
                            <TextField
                                select
                                value={hours}
                                onChange={(e) => handleTimeChange('hours', e.target.value)}
                                sx={{ 
                                    'width': '80px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    }
                                }}
                            >
                                {hourOptions.map((hour) => (
                                    <MenuItem key={hour} value={hour}>
                                        {hour}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        
                        <Box>
                            <TextField
                                select
                                value={minutes}
                                onChange={(e) => handleTimeChange('minutes', e.target.value)}
                                sx={{ 
                                    'width': '80px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    }
                                }}
                            >
                                {minuteOptions.map((minute) => (
                                    <MenuItem key={minute} value={minute}>
                                        {minute}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Box>
                </Box>

                {helpText && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {i18n.t(`helpText.${helpText}`)}
                    </Typography>
                )}

                {needDivider && <Divider sx={{ mb: 2 }} />}
            </Box>
        </LocalizationProvider>
    );
};
